
import {Component, Prop, Watch} from "vue-property-decorator";
import {HcPageModel} from "@dev/hc-components";
import TemplateOptions from "@/module/main/page/TemplateOptionsEnum";
import Culture from "@/module/main/page/CultureEnum";
import Block1 from "@/module/main/components/Block1.vue";
import Block2Income from "@/module/main/components/Block2Income.vue";
import ICurrency from "@/module/main/dto/ICurrency";
import ICustomer from "@/module/main/model/applicationForm/ICustomer";
import Block2Others from "@/module/main/components/Block2Others.vue";
import Block3Others from "@/module/main/components/Block3Others.vue";
import Block3Address from "@/module/main/components/Block3Address.vue";
import Block3Employee from "@/module/main/components/Block3Employee.vue";
import Block3Entrepreneur from "@/module/main/components/Block3Entrepreneur.vue";
import IncomeType from "@/module/main/dto/customer/IncomeTypeEnum";
import {EnumUtil} from "@/app";
import OrderItems from "@/module/main/components/OrderItems.vue";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import Broker from "@/module/main/components/Broker.vue";
import Entrepreneur from "@/module/main/components/Entrepreneur.vue";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import CarLoan from "@/module/main/components/CarLoan.vue";
import CarLoanType from "@/module/main/dto/CarLoanTypeEnum";

@Component({
    components: {
        Block1,
        Block2Income,
        Block2Others,
        Block3Others,
        Block3Address,
        Block3Employee,
        Block3Entrepreneur,
        OrderItems,
        Broker,
        Entrepreneur,
        CarLoan
    }
})
export default class Customer extends HcPageModel {
    @Prop({type: Object}) public value!: ICustomer;
    @Prop({type: String}) public culture!: Culture;
    @Prop({type: String}) public template!: TemplateOptions;
    @Prop({type: Object}) public currency!: ICurrency;

    protected pageName: string = "customer";

    private tabBlock1: string = "Block1";
    private tabOrderItems: string = "OrderItems";
    private tabBroker: string = "Broker";
    private tabEntrepreneur: string = "Entrepreneur";
    private tabCarLoan: string = "CarLoan";
    private defaultTabs: string[] = [this.tabBlock1, "Block2Income", "Block2Others", "Block3Others", "Block3Address"];
    private currentTab: string = this.tabBlock1;
    private tabs: string[] = [];

    @Watch("value.financedSubjectType")
    private watchFinancedSubjectType(
        newValue: FinancedSubjectType,
        oldValue: FinancedSubjectType
    ): void {
        this.prepareTabs();
    }
    @Watch("value.orderType")
    private watchOrderType(
        newValue: OrderType,
        oldValue: OrderType
    ): void {
        this.prepareTabs();
    }
    @Watch("value.carLoanType")
    private watchCarLoanType(
        newValue: CarLoanType,
        oldValue: CarLoanType
    ): void {
        this.prepareTabs();
    }
    @Watch("value.clientType")
    private watchClientType(
        newValue: ClientType,
        oldValue: ClientType
    ): void {
        this.prepareTabs();
    }

    private get currentProperties(): object {
        return {
            culture: this.culture,
            template: this.template,
            currency: this.currency,
            ...(this.currentTab === this.tabCarLoan ? {carLoanType: this.value.carLoanType} : {}),
        }
    }

    private get currentModel(): object {
        let model: object;

        switch (this.currentTab) {
            case "Block1":
                model = this.value.block1;
                break;
            case "Block2Income":
                model = this.value.block2Income;
                break;
            case "Block2Others":
                model = this.value.block2Others;
                break;
            case "Block3Others":
                model = this.value.block3Others;
                break;
            case "Block3Address":
                model = this.value.block3Address;
                break;
            case "Block3Employee":
                model = this.value.block3Employee;
                break;
            case "Block3Entrepreneur":
                model = this.value.block3Entrepreneur;
                break;
            case "OrderItems":
                model = this.value.orderItems;
                break;
            case "Broker":
                model = this.value.broker;
                break;
            case "Entrepreneur":
                model = this.value.entrepreneur;
                break;
            case "CarLoan":
                model = this.value.carLoan;
                break;
            default:
                model = {}
        }

        return model;
    }

    private prepareTabs(): void
    {
        let financedSubjectType = this.value.financedSubjectType;
        let orderType = this.value.orderType;
        let clientType = this.value.clientType;

        if (financedSubjectType === FinancedSubjectType.ORDER) {
            switch (orderType) {
                case OrderType.STANDARD:
                    this.tabs = clientType === ClientType.PERSON ? [...this.defaultTabs] : [this.tabEntrepreneur];
                    this.addTab(this.tabOrderItems);
                    break;
                case OrderType.BROKERED:
                    this.tabs = [this.tabBlock1, "Block3Address", this.tabOrderItems, this.tabBroker];
                    break;
                case OrderType.OPERATING_LEASE:
                    this.tabs = clientType === ClientType.PERSON ? [...this.defaultTabs] : [this.tabEntrepreneur];
                    break;
                case OrderType.CAR_LOAN:
                    this.tabs = this.value.carLoanType === CarLoanType.LARGE_CARS
                        ? [...this.defaultTabs, this.tabCarLoan]
                        : [this.tabCarLoan];
                    break;
                default:
                    break;
            }
        } else if (financedSubjectType === FinancedSubjectType.WALLET) {
            this.tabs = [...this.defaultTabs];
            this.addTab(this.tabOrderItems);
        } else {
            this.tabs = [...this.defaultTabs];
        }

        this.checkAndRepairCurrentTab();
    }

    private checkAndRepairCurrentTab()
    {
        const index: number = this.tabs.indexOf(this.currentTab);
        if (index === -1)
            this.currentTab = this.tabs[0];
    }

    private incomeTypeChanged(value: any): void {
        const incomeType: IncomeType = EnumUtil.getEnumValue(IncomeType, value);

        switch (incomeType) {
            case IncomeType.EMPLOYEE:
                this.removeTab("Block3Entrepreneur");
                this.addTab("Block3Employee");
                break;
            case IncomeType.ENTREPRENEUR:
                this.removeTab("Block3Employee");
                this.addTab("Block3Entrepreneur");
                break;
            default:
                this.removeTab("Block3Entrepreneur");
                this.removeTab("Block3Employee");
        }
    }

    private removeTab(remove: string): void {
        const index: number = this.tabs.indexOf(remove);
        if (index > -1) {
            this.tabs.splice(index, 1);
        }
    }

    private addTab(add: string): void {
        const index: number = this.tabs.indexOf(add);
        if (index == -1) {
            this.tabs.push(add);
        }
    }

    public mounted(): void
    {
        this.prepareTabs();
    }
}
